import { MessageSquare, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/client/components/ui/button";
import { useAgent } from "@/client/hooks/use-agent";

const ROUTE_HINTS: Record<string, string> = {
  "/": "I can help you understand your portfolio",
  "/insurance": "Need help finding a record?",
  "/settings": "Need help with settings?",
  "/comms": "I can help with communications",
};

const ONBOARDING_KEY = "agent-onboarding-seen";

function getRouteHint(pathname: string): string {
  if (pathname.match(/^\/insurance\/\d+$/)) {
    return "Ask me about this record";
  }
  if (pathname.match(/^\/insurance\/\d+\/edit$/)) {
    return "I can help fill in fields";
  }
  return ROUTE_HINTS[pathname] ?? "Ask me anything";
}

export function AgentButton(): React.ReactElement | null {
  const { isOpen, toggle } = useAgent();
  const location = useLocation();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const hint = getRouteHint(location.pathname);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [toggle]);

  useEffect(() => {
    if (isOpen) {
      setShowOnboarding(false);
      return;
    }

    const seen = localStorage.getItem(ONBOARDING_KEY);
    if (!seen) {
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const dismissOnboarding = (): void => {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_KEY, "true");
  };

  const handleClick = (): void => {
    dismissOnboarding();
    toggle();
  };

  if (isOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 z-30" data-testid="agent-button-container">
      {/* Onboarding callout */}
      {showOnboarding && (
        <div className="absolute bottom-16 right-0 w-64 rounded-lg border bg-card p-3 shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-300">
          <button
            type="button"
            onClick={dismissOnboarding}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
            data-testid="agent-onboarding-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <p className="text-sm font-medium text-foreground">Meet your assistant</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Search records, get summaries, and make changes — all in plain English. Click to get started.
          </p>
          <div className="mt-2 text-[10px] text-muted-foreground/60">Keyboard shortcut: Ctrl+K</div>
          <div className="absolute -bottom-1.5 right-5 h-3 w-3 rotate-45 border-b border-r bg-card" />
        </div>
      )}

      {/* Tooltip */}
      {showTooltip && !showOnboarding && (
        <div className="absolute bottom-14 right-0 whitespace-nowrap rounded-md bg-foreground px-2.5 py-1.5 text-xs text-background shadow-md">
          {hint}
          <div className="absolute -bottom-1 right-5 h-2 w-2 rotate-45 bg-foreground" />
        </div>
      )}

      <Button
        size="icon"
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="h-12 w-12 rounded-full shadow-lg"
        data-testid="agent-toggle-button"
      >
        <MessageSquare className="h-5 w-5" />
      </Button>
    </div>
  );
}
